import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import {
  Lock,
  Crown,
  Loader2,
  Play,
  Pause,
  Save,
  TrendingUp,
  DollarSign,
  Target,
  Zap
} from 'lucide-react';

function NumberField({ label, value, onChange, min, max, step, hint }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input"
        min={min}
        max={max}
        step={step}
      />
      {hint && <p className="mt-1 text-xs text-dark-500">{hint}</p>}
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color = 'text-primary-400' }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-dark-800 ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-dark-400">{title}</p>
          <p className="text-lg font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

export default function Arbitrage() {
  const [tier, setTier] = useState(null);
  const [isTierLoading, setIsTierLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState('');

  const isWhale = (tier || 'free') === 'whale';

  useEffect(() => {
    let canceled = false;
    const fetchTier = async () => {
      setIsTierLoading(true);
      try {
        const res = await api.get('/payments/subscription');
        if (!canceled) setTier(res.data?.effectiveTier || res.data?.tier || 'free');
      } catch {
        if (!canceled) setTier('free');
      } finally {
        if (!canceled) setIsTierLoading(false);
      }
    };
    fetchTier();
    return () => { canceled = true; };
  }, []);

  const fetchConfig = async () => {
    setError('');
    setMessage(null);
    setIsConfigLoading(true);
    try {
      const res = await api.get('/arbitrage/config');
      setConfig(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to fetch arbitrage config');
    } finally {
      setIsConfigLoading(false);
    }
  };

  useEffect(() => {
    if (!isWhale) return;
    fetchConfig();
  }, [isWhale]);

  const draft = useMemo(() => {
    if (!config) return null;
    const arb = config.arbitrage || {};
    const risk = config.risk || {};
    const execution = config.execution || {};
    const universe = config.universe || {};

    return {
      isActive: !!config.isActive,
      mode: config.mode || 'paper',
      universe: {
        maxHoursToExpiry: universe.maxHoursToExpiry ?? 24,
        minHoursToExpiry: universe.minHoursToExpiry ?? 0.25,
        maxMarketsPerTick: universe.maxMarketsPerTick ?? 10
      },
      arbitrage: {
        targetPairCost: arb.targetPairCost ?? 0.96,
        maxPairCost: arb.maxPairCost ?? 0.98,
        minDiscountThreshold: arb.minDiscountThreshold ?? 0.48,
        maxImbalanceRatio: arb.maxImbalanceRatio ?? 1.5
      },
      risk: {
        maxCapitalPerMarket: risk.maxCapitalPerMarket ?? 500,
        maxTotalCapital: risk.maxTotalCapital ?? 2000,
        maxOpenPositions: risk.maxOpenPositions ?? 5,
        cooldownSeconds: risk.cooldownSeconds ?? 10
      },
      execution: {
        baseOrderSize: execution.baseOrderSize ?? 25,
        maxOrderSize: execution.maxOrderSize ?? 100
      }
    };
  }, [config]);

  const [form, setForm] = useState(null);

  useEffect(() => {
    if (draft) setForm(draft);
  }, [draft]);

  const updateNested = (path, value) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const [root, key] = path.split('.');
      next[root] = { ...(next[root] || {}), [key]: value };
      return next;
    });
  };

  const saveConfig = async () => {
    if (!form) return;
    setError('');
    setMessage(null);
    setIsSaving(true);
    try {
      const payload = {
        isActive: form.isActive,
        mode: form.mode,
        universe: {
          maxHoursToExpiry: Number(form.universe.maxHoursToExpiry),
          minHoursToExpiry: Number(form.universe.minHoursToExpiry),
          maxMarketsPerTick: Number(form.universe.maxMarketsPerTick)
        },
        arbitrage: {
          targetPairCost: Number(form.arbitrage.targetPairCost),
          maxPairCost: Number(form.arbitrage.maxPairCost),
          minDiscountThreshold: Number(form.arbitrage.minDiscountThreshold),
          maxImbalanceRatio: Number(form.arbitrage.maxImbalanceRatio)
        },
        risk: {
          maxCapitalPerMarket: Number(form.risk.maxCapitalPerMarket),
          maxTotalCapital: Number(form.risk.maxTotalCapital),
          maxOpenPositions: Number(form.risk.maxOpenPositions),
          cooldownSeconds: Number(form.risk.cooldownSeconds)
        },
        execution: {
          baseOrderSize: Number(form.execution.baseOrderSize),
          maxOrderSize: Number(form.execution.maxOrderSize)
        }
      };

      const res = await api.put('/arbitrage/config', payload);
      setConfig(res.data);
      setMessage({ type: 'success', text: 'Arbitrage settings saved.' });
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async () => {
    setError('');
    setMessage(null);
    setIsToggling(true);
    try {
      const res = await api.post('/arbitrage/toggle');
      setConfig((prev) => prev ? { ...prev, isActive: res.data.isActive } : prev);
      setMessage({ 
        type: 'success', 
        text: res.data.isActive ? 'Arbitrage mode started.' : 'Arbitrage mode stopped.' 
      });
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to toggle arbitrage');
    } finally {
      setIsToggling(false);
    }
  };

  if (isTierLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  if (!isWhale) {
    return (
      <div className="max-w-2xl mx-auto mt-12">
        <div className="card p-8 text-center">
          <Lock className="w-16 h-16 mx-auto mb-4 text-dark-500" />
          <h2 className="text-2xl font-bold text-white mb-2">Whale Tier Required</h2>
          <p className="text-dark-400 mb-6">
            Binary Arbitrage Mode is an exclusive feature for Whale tier subscribers.
            This strategy automatically buys both YES and NO when prices are cheap,
            locking in guaranteed profit regardless of market outcome.
          </p>
          <Link to="/pricing" className="btn btn-primary inline-flex items-center gap-2">
            <Crown className="w-4 h-4" />
            Upgrade to Whale
          </Link>
        </div>
      </div>
    );
  }

  if (isConfigLoading || !form) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  const stats = config?.stats || {};

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Zap className="w-6 h-6 text-yellow-400" />
            Binary Arbitrage
          </h1>
          <p className="text-dark-400 text-sm mt-1">
            Gabagool-style strategy: Buy both sides cheap, lock in profit
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleActive}
            disabled={isToggling}
            className={`btn ${config?.isActive ? 'btn-danger' : 'btn-primary'} flex items-center gap-2`}
          >
            {isToggling ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : config?.isActive ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {config?.isActive ? 'Stop' : 'Start'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {message && (
        <div className={`rounded-lg p-4 ${
          message.type === 'success' 
            ? 'bg-green-500/10 border border-green-500/20 text-green-400'
            : 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400'
        }`}>
          {message.text}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Trades" 
          value={stats.totalTrades || 0} 
          icon={TrendingUp}
        />
        <StatCard 
          title="Total Profit" 
          value={`$${(stats.totalProfit || 0).toFixed(2)}`} 
          icon={DollarSign}
          color="text-green-400"
        />
        <StatCard 
          title="Markets Completed" 
          value={stats.marketsCompleted || 0} 
          icon={Target}
        />
        <StatCard 
          title="Win Rate" 
          value={`${((stats.winRate || 0) * 100).toFixed(0)}%`} 
          icon={Zap}
          color="text-yellow-400"
        />
      </div>

      {/* Strategy Explanation */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-white mb-3">How It Works</h3>
        <div className="space-y-2 text-sm text-dark-300">
          <p>
            <strong className="text-white">1.</strong> Find short-duration binary markets (15-min to 1-hour crypto)
          </p>
          <p>
            <strong className="text-white">2.</strong> Buy YES when YES price is cheap (below threshold)
          </p>
          <p>
            <strong className="text-white">3.</strong> Buy NO when NO price is cheap (below threshold)
          </p>
          <p>
            <strong className="text-white">4.</strong> Keep avg(YES) + avg(NO) &lt; $1.00
          </p>
          <p>
            <strong className="text-white">5.</strong> At settlement, one side pays $1 → guaranteed profit
          </p>
        </div>
      </div>

      {/* Configuration */}
      <div className="card p-6 space-y-6">
        <h3 className="text-lg font-semibold text-white">Configuration</h3>

        {/* Arbitrage Parameters */}
        <div>
          <h4 className="text-sm font-medium text-dark-300 mb-3">Arbitrage Parameters</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <NumberField
              label="Target Pair Cost"
              value={form.arbitrage.targetPairCost}
              onChange={(v) => updateNested('arbitrage.targetPairCost', v)}
              min={0.90}
              max={0.99}
              step={0.01}
              hint="Target avg(YES)+avg(NO)"
            />
            <NumberField
              label="Max Pair Cost"
              value={form.arbitrage.maxPairCost}
              onChange={(v) => updateNested('arbitrage.maxPairCost', v)}
              min={0.90}
              max={0.99}
              step={0.01}
              hint="Safety limit"
            />
            <NumberField
              label="Discount Threshold"
              value={form.arbitrage.minDiscountThreshold}
              onChange={(v) => updateNested('arbitrage.minDiscountThreshold', v)}
              min={0.30}
              max={0.50}
              step={0.01}
              hint="Only buy if price < this"
            />
            <NumberField
              label="Max Imbalance"
              value={form.arbitrage.maxImbalanceRatio}
              onChange={(v) => updateNested('arbitrage.maxImbalanceRatio', v)}
              min={1.0}
              max={3.0}
              step={0.1}
              hint="Max YES:NO ratio"
            />
          </div>
        </div>

        {/* Market Selection */}
        <div>
          <h4 className="text-sm font-medium text-dark-300 mb-3">Market Selection</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <NumberField
              label="Max Hours to Expiry"
              value={form.universe.maxHoursToExpiry}
              onChange={(v) => updateNested('universe.maxHoursToExpiry', v)}
              min={0.25}
              max={24}
              step={0.25}
              hint="Focus on short markets"
            />
            <NumberField
              label="Min Hours to Expiry"
              value={form.universe.minHoursToExpiry}
              onChange={(v) => updateNested('universe.minHoursToExpiry', v)}
              min={0.01}
              max={1}
              step={0.01}
              hint="Don't enter too late"
            />
            <NumberField
              label="Max Markets"
              value={form.universe.maxMarketsPerTick}
              onChange={(v) => updateNested('universe.maxMarketsPerTick', v)}
              min={1}
              max={20}
              step={1}
              hint="Per tick"
            />
          </div>
        </div>

        {/* Risk Management */}
        <div>
          <h4 className="text-sm font-medium text-dark-300 mb-3">Risk Management</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <NumberField
              label="Max Capital/Market"
              value={form.risk.maxCapitalPerMarket}
              onChange={(v) => updateNested('risk.maxCapitalPerMarket', v)}
              min={50}
              max={5000}
              step={50}
              hint="$ per market"
            />
            <NumberField
              label="Max Total Capital"
              value={form.risk.maxTotalCapital}
              onChange={(v) => updateNested('risk.maxTotalCapital', v)}
              min={100}
              max={20000}
              step={100}
              hint="$ across all markets"
            />
            <NumberField
              label="Max Open Positions"
              value={form.risk.maxOpenPositions}
              onChange={(v) => updateNested('risk.maxOpenPositions', v)}
              min={1}
              max={20}
              step={1}
            />
            <NumberField
              label="Cooldown (sec)"
              value={form.risk.cooldownSeconds}
              onChange={(v) => updateNested('risk.cooldownSeconds', v)}
              min={5}
              max={120}
              step={5}
              hint="Between buys"
            />
          </div>
        </div>

        {/* Execution */}
        <div>
          <h4 className="text-sm font-medium text-dark-300 mb-3">Execution</h4>
          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label="Base Order Size"
              value={form.execution.baseOrderSize}
              onChange={(v) => updateNested('execution.baseOrderSize', v)}
              min={5}
              max={500}
              step={5}
              hint="Shares per buy"
            />
            <NumberField
              label="Max Order Size"
              value={form.execution.maxOrderSize}
              onChange={(v) => updateNested('execution.maxOrderSize', v)}
              min={10}
              max={1000}
              step={10}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={saveConfig}
            disabled={isSaving}
            className="btn btn-primary flex items-center gap-2"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Settings
          </button>
        </div>
      </div>

      {/* Status */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <span className="text-dark-400">Status</span>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            config?.isActive 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-dark-700 text-dark-400'
          }`}>
            {config?.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-dark-400">Mode</span>
          <span className="text-white">{form.mode === 'paper' ? 'Paper Trading' : 'Real Trading'}</span>
        </div>
      </div>
    </div>
  );
}
